import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import crypto from 'crypto-js';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

function Content2Learn() {
  console.log('Content2Learn component rendering');

  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [writingStyle, setWritingStyle] = useState('');
  const [language, setLanguage] = useState('en');
  const [currentStep, setCurrentStep] = useState('');
  const [platform, setPlatform] = useState('');

  const getLanguageName = (code) => {
    const languageMap = {
      'en': 'English',
      'zh': 'Chinese',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean'
    };
    return languageMap[code] || 'English';
  };
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [transcript, setTranscript] = useState('');
  const [processedUrl, setProcessedUrl] = useState('');
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isPublic, setIsPublic] = useState(true);
  const [videoId, setVideoId] = useState('');
  const [showExamples, setShowExamples] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    // 尝试从 iframe 的标题中获取视频标题
    if (iframeRef.current) {
      try {
        const iframeWindow = iframeRef.current.contentWindow;
        // 监听来自 iframe 的消息
        window.addEventListener('message', (event) => {
          if (event.origin === 'https://player.bilibili.com') {
            const data = event.data;
            if (data && data.title) {
              setTitle(data.title);
            }
          }
        });
      } catch (error) {
        console.error('Error accessing iframe:', error);
      }
    }
  };

  const examples = [
    {
      url: 'https://youtu.be/F7PxEy5IyV4',
      title: 'Understanding AI',
      thumbnail: `https://img.youtube.com/vi/F7PxEy5IyV4/mqdefault.jpg`
    },
    {
      url: 'https://youtu.be/kv8frWeKoeo',
      title: 'Machine Learning Basics',
      thumbnail: `https://img.youtube.com/vi/kv8frWeKoeo/mqdefault.jpg`
    },
    {
      url: 'https://youtu.be/FraQpapjQ18',
      title: 'Deep Learning Introduction',
      thumbnail: `https://img.youtube.com/vi/FraQpapjQ18/mqdefault.jpg`
    }
  ];

  const extractVideoInfo = (url) => {
    console.log('Extracting video info from URL:', url);
    
    // YouTube URL patterns
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const youtubeMatch = url.match(youtubeRegExp);
    
    // Bilibili URL patterns (support both www.bilibili.com and b23.tv)
    // More lenient pattern that looks for BV ID anywhere in the URL
    const bilibiliRegExp = /bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/i;
    const biliShortRegExp = /b23\.tv\/(BV[a-zA-Z0-9]+)/i;
    
    console.log('Bilibili regex pattern:', bilibiliRegExp.toString());
    
    const bilibiliMatch = url.match(bilibiliRegExp);
    const biliShortMatch = url.match(biliShortRegExp);
    
    console.log('Bilibili match result:', bilibiliMatch);
    console.log('Bilibili short match result:', biliShortMatch);

    if (youtubeMatch && youtubeMatch[2].length === 11) {
      return { platform: 'youtube', id: youtubeMatch[2] };
    } else if (bilibiliMatch) {
      const bvid = bilibiliMatch[1];
      console.log('Extracted Bilibili video ID:', bvid); // Debug log
      return { platform: 'bilibili', id: bvid };
    } else if (biliShortMatch) {
      const bvid = biliShortMatch[1];
      console.log('Extracted Bilibili short URL video ID:', bvid); // Debug log
      return { platform: 'bilibili', id: bvid };
    }
    
    console.log('No match found for URL:', url);
    return null;
  };

  useEffect(() => {
    if (!url) return;
    
    console.log('URL changed, extracting video info from:', url);
    const videoInfo = extractVideoInfo(url);
    console.log('Extracted video info:', videoInfo);
    
    if (videoInfo) {
      setVideoId(videoInfo.id);
      setPlatform(videoInfo.platform);
      console.log('Set videoId:', videoInfo.id, 'and platform:', videoInfo.platform);

      if (videoInfo.platform === 'youtube') {
        // Fetch YouTube video title
        fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoInfo.id}`)
          .then(response => response.json())
          .then(data => {
            if (data.title) {
              setTitle(data.title);
            }
          })
          .catch(error => console.error('Error fetching video title:', error));
      } else if (videoInfo.platform === 'bilibili') {
        console.log('Setting up Bilibili iframe for video:', videoInfo.id);
        // 使用 iframe 方式，标题将通过 iframe 加载事件获取
        setVideoId(videoInfo.id);
        setPlatform('bilibili');
        
        // Try to get video info from our server API first
        fetch(`/api/bilibili/info?bvid=${videoInfo.id}`)
          .then(response => {
            if (!response.ok) {
              throw new Error('Failed to fetch from API');
            }
            return response.json();
          })
          .then(data => {
            console.log('Bilibili API response:', data);
            if (data.title) {
              setTitle(data.title);
              console.log('Set Bilibili title from API:', data.title);
            }
          })
          .catch(error => {
            console.error('Error fetching from Bilibili API, falling back to HTML scraping:', error);
            
            // Fallback: 尝试从 HTML 页面获取标题
            fetch(`https://www.bilibili.com/video/${videoInfo.id}`, {
              headers: {
                'Accept': 'text/html',
                'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
              }
            })
              .then(response => response.text())
              .then(html => {
                const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/);
                if (titleMatch && titleMatch[1]) {
                  const title = titleMatch[1].replace(' - 哔哩哔哩', '').trim();
                  setTitle(title);
                  console.log('Set Bilibili title from HTML:', title);
                } else {
                  console.log('Could not extract title from HTML:', html.substring(0, 200) + '...');
                  // If HTML scraping fails, use default title
                  setTitle(`Bilibili Video ${videoInfo.id}`);
                }
              })
              .catch(error => {
                console.error('Error fetching Bilibili page:', error);
                // If title fetch fails, use a default title
                setTitle(`Bilibili Video ${videoInfo.id}`);
                console.log('Using fallback title for Bilibili video');
              });
          });
      }
    } else {
      // Clear video ID and platform if no valid video info found
      setVideoId('');
      setPlatform('');
      console.log('No valid video info found, cleared videoId and platform');
    }
  }, [url]);

  useEffect(() => {
    console.log('Content2Learn mounted');
    console.log('Current auth state:', {
      currentUser,
      authCurrentUser: auth.currentUser,
      isAuthenticated: !!currentUser,
      uid: currentUser?.uid,
      email: currentUser?.email
    });
  }, []);

  useEffect(() => {
    const detectBrowserLanguage = () => {
      const browserLang = navigator.language.split('-')[0];
      const supportedLanguages = ['en', 'zh', 'es', 'fr', 'de'];
      const defaultLang = 'en';
      
      // Check if browser language is supported, otherwise use default
      const detectedLang = supportedLanguages.includes(browserLang) ? browserLang : defaultLang;
      
      // First try to get saved preference, if not available use detected language
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (!savedLanguage) {
        setLanguage(detectedLang);
        localStorage.setItem('preferredLanguage', detectedLang);
      } else {
        setLanguage(savedLanguage);
      }
    };

    detectBrowserLanguage();
  }, []);

  const resetUI = () => {
    setTranscript('');
    setProgress(0);
    setStatusMessage('');
    setError('');
    setCurrentStep('');
    setProcessedUrl('');
  };

  const showCompletionMessage = () => {
    setStatusMessage('Processing completed successfully! 🎉 Your content is ready at: ' + processedUrl);
    setProgress(100);
    setCurrentStep('html');
    
    // Create confetti effect
    const confetti = document.createElement('div');
    confetti.className = 'fixed inset-0 pointer-events-none z-50';
    confetti.innerHTML = Array(50).fill().map(() => `
      <div class="absolute animate-confetti" style="
        left: ${Math.random() * 100}%;
        animation-delay: ${Math.random() * 3}s;
        background: ${['#ff0080', '#ff0000', '#00ff00', '#0000ff', '#ffff00'][Math.floor(Math.random() * 5)]};
      "></div>
    `).join('');
    document.body.appendChild(confetti);
    
    // Create checkmark animation
    const checkmark = document.createElement('div');
    checkmark.className = 'fixed bottom-4 right-4 bg-green-500 text-white rounded-full p-4 shadow-lg z-50 animate-bounce';
    checkmark.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
      </svg>
    `;
    document.body.appendChild(checkmark);
    
    // Remove confetti and checkmark after animation
    setTimeout(() => {
      document.body.removeChild(confetti);
      document.body.removeChild(checkmark);
      setStatusMessage('');
      // window.location.href = processedUrl; // 注释掉这行，避免双重导航
    }, 5000); // 减少到5秒
  };

  const processContent = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError('Please login to process content');
      return;
    }

    if (!writingStyle) {
      setError('Please select a writing style');
      return;
    }

    setIsProcessing(true);
    setError('');
    resetUI();
    setShowExamples(false);

    try {
      const response = await fetch('https://xihajun.tplinkdns.com/process-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          url, 
          writing_style: writingStyle,
          language: language 
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          try {
            const data = JSON.parse(line);
            handleProcessingUpdate(data);

            if (data.action === "CLOSE") {
              // Extract URL from the result message
              const urlMatch = data.result.match(/https?:\/\/[^\s"]+/);
              if (urlMatch) {
                const processedUrl = urlMatch[0];
                setProcessedUrl(processedUrl);
                
                // Extract content ID from the URL and combine with style and language
                const baseId = processedUrl.split('/').pop().replace('/index.html', '');
                const contentId = `${baseId}_${writingStyle}_${language}`;
                
                const contentData = {
                  userId: currentUser.uid,
                  createdAt: new Date().toISOString(),
                  isPublic: isPublic,
                  title: title.trim() || 'Untitled',
                  originalUrl: url,
                  writingStyle: writingStyle,
                  language: language,
                  externalUrl: processedUrl,
                  contentId: contentId // Store the combined ID
                };
                
                const contentRef = await addDoc(collection(db, 'contents'), contentData);
                // Navigate to UserHistory after successful content creation
                navigate('/history');
                showCompletionMessage();
              }
              return;
            }
          } catch (error) {
            console.error('Error parsing JSON:', error);
          }
        }
      }
    } catch (error) {
      setError(`Error processing content: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessingUpdate = (data) => {
    setStatusMessage(`${data.action}: ${data.result}`);

    switch (data.action) {
      case "Extracting ID":
        setCurrentStep('extract');
        setProgress(25);
        break;
      case "Transcript Fetched":
      case "Transcript Generated":
      case "Fetching Transcript":
        setCurrentStep('transcript');
        setTranscript(data.result);
        setProgress(50);
        break;
      case "Generating Tutorial":
        setCurrentStep('tutorial');
        setProgress(75);
        break;
      case "Generating HTML":
        if (data.result.includes("successfully")) {
          setCurrentStep('html');
          setProgress(90);
        }
        break;
      case "Error":
        setError(data.result);
        setCurrentStep('');
        setProgress(0);
        break;
      case "CLOSE":
        setProgress(100);
        break;
    }
  };

  // Generate fake content based on writing style
  const generateFakeContent = () => {
    const sections = [
      `## Section 1: Introduction to ${writingStyle} Style

In this section, we'll explore the basics of ${writingStyle} writing style.

### Key Concepts
- Understanding the fundamentals
- Basic principles of ${writingStyle}
- Getting started with examples

### Practice Exercise
\`\`\`javascript
// Try this simple example
function greet() {
  console.log("Hello from ${writingStyle} style!");
}
\`\`\`

> Remember: Practice makes perfect!

---`,

      `## Section 2: Advanced ${writingStyle} Techniques

Let's dive deeper into advanced concepts.

### Advanced Topics
* Professional techniques
* Best practices
* Common pitfalls to avoid

### Code Example
\`\`\`javascript
// Advanced implementation
function advancedExample() {
  const techniques = ["practice", "learn", "improve"];
  techniques.forEach(t => console.log(\`Time to \${t}!\`));
}
\`\`\`

> Pro tip: Always keep learning and experimenting.

---`,

      `## Section 3: Real-world Applications

Now let's see how to apply these concepts in real situations.

### Practical Applications
* Industry examples
* Case studies
* Success stories

### Implementation
\`\`\`javascript
// Real-world example
class RealWorldApp {
  constructor() {
    this.style = "${writingStyle}";
  }
  
  apply() {
    console.log("Applying concepts in real world");
  }
}
\`\`\`

> Note: Real-world applications require practice and patience.

---`
    ];
    
    return sections;
  };

  // 添加 simulateStep 函数定义
  const simulateStep = async (action, result, delay) => {
    await new Promise(resolve => setTimeout(resolve, delay));
    handleProcessingUpdate({ action, result });
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg max-w-md shadow-lg">
          <h2 className="text-2xl font-bold mb-4 text-teal-600 dark:text-teal-400">Please Login</h2>
          <p className="mb-6 text-gray-700 dark:text-gray-300">You need to be logged in to use Content2Learn</p>
          <Link 
            to="/login" 
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded inline-block"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-6xl mx-auto px-4 py-4 sm:py-8">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-6 sm:mb-10 text-teal-600 dark:text-teal-400 tracking-tight">
          {isProcessing && title ? title.trim() || 'Untitled' : 'Content2Learn'}
        </h1>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm dark:shadow-2xl mb-8 sm:mb-12 border border-gray-200 dark:border-gray-700">
          <form onSubmit={processContent} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-grow">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 rounded-lg px-4 py-3 pl-10 pr-[120px] focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all border border-gray-200 dark:border-gray-600 text-base"
                  placeholder="Enter YouTube or Bilibili URL"
                  required
                />
                <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                
                {/* 将开关放到输入框内的右侧 */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPublic(!isPublic);
                    }}
                    className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                      isPublic 
                        ? 'text-teal-600 dark:text-teal-400 hover:text-teal-500 dark:hover:text-teal-300 hover:bg-teal-400/10' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-400/10'
                    }`}
                    title={isPublic ? 'Content will be public' : 'Content will be private'}
                    aria-label={isPublic ? 'Make Private' : 'Make Public'}
                  >
                    {isPublic ? (
                      <EyeIcon className="h-5 w-5" />
                    ) : (
                      <EyeSlashIcon className="h-5 w-5" />
                    )}
                  </button>
                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                    {isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <select
                value={writingStyle}
                onChange={(e) => setWritingStyle(e.target.value)}
                className="flex-1 bg-white dark:bg-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 text-base"
                required
              >
                <option value="">Select Writing Style</option>
                <option value="10-year-old">Simple & Easy (10-year-old)</option>
                <option value="DAN">Creative & Unrestricted (DAN)</option>
                <option value="narrative">Story-based Learning</option>
                <option value="humorous">Fun & Engaging</option>
                <option value="technical">Technical & Detailed</option>
              </select>

              <select
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  localStorage.setItem('preferredLanguage', e.target.value);
                }}
                className="flex-1 bg-white dark:bg-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 text-base"
                required
              >
                <option value="en">English</option>
                <option value="zh">中文</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="pt">Português</option>
                <option value="ru">Русский</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
              </select>
              
              <button
                type="submit"
                disabled={isProcessing}
                className="bg-teal-600 hover:bg-teal-500 focus:ring-2 focus:ring-offset-2 focus:ring-teal-400 text-white font-medium py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Transform Video'
                )}
              </button>
            </div>
          </form>

          {/* Example Videos Section */}
          <div className="mt-6 sm:mt-8 border-t border-gray-200 dark:border-gray-700/50 pt-4 sm:pt-6">
            <button 
              onClick={() => setShowExamples(!showExamples)} 
              className="flex items-center gap-2 text-base sm:text-lg font-semibold text-teal-600 dark:text-teal-400 mb-4 sm:mb-6 hover:text-teal-500 dark:hover:text-teal-300 transition-colors group w-full sm:w-auto"
            >
              <span>Try these examples</span>
              <svg 
                className={`w-5 h-5 transform transition-transform group-hover:translate-x-1 ${showExamples ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {showExamples && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setUrl(examples[index].url);
                    setWritingStyle('humorous');
                    setShowExamples(false);
                  }}
                  className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 active:scale-95 hover:shadow-md dark:hover:shadow-xl group border border-gray-200 dark:border-gray-700 touch-manipulation"
                >
                  <div className="relative">
                    <img
                      src={examples[index].thumbnail}
                      alt={examples[index].title}
                      className="w-full aspect-video object-cover group-hover:opacity-90 transition-opacity"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4">
                    <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors truncate" title={examples[index].title || 'Untitled'}>
                      {examples[index].title || 'Untitled'}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      Click to try with humorous style
                    </p>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>

        {/* Floating Progress Modal - Mobile Optimized */}
        {isProcessing && (
          <div className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:right-6 sm:left-auto bg-white dark:bg-gray-800 backdrop-blur-lg rounded-xl p-4 sm:p-6 shadow-md dark:shadow-2xl w-auto sm:w-96 transition-all duration-300 ease-in-out z-[9999] border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-teal-600 dark:text-teal-400">Processing Status</h3>
              <div className="text-sm font-medium px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400">
                {Math.round(progress)}%
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              {['extract', 'transcript', 'tutorial', 'html'].map((step) => (
                <div key={step} className="flex items-center gap-3">
                  <div 
                    className={`w-3 h-3 rounded-full ${currentStep === step
                      ? 'bg-teal-500 dark:bg-teal-400 ring-4 ring-teal-50 dark:ring-teal-400/20 animate-pulse'
                      : progress > getStepProgress(step)
                      ? 'bg-purple-500 dark:bg-purple-400'
                      : error ? 'bg-red-500 dark:bg-red-400' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  />
                  <span className={`text-sm font-medium ${currentStep === step ? 'text-teal-600 dark:text-teal-400' : error ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {step.charAt(0).toUpperCase() + step.slice(1)}
                  </span>
                </div>
              ))}
            </div>

            {statusMessage && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                {statusMessage}
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 sm:p-4 bg-red-50 dark:bg-red-500/20 border border-red-100 dark:border-red-500/50 rounded-lg text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
            
            {/* Close button for mobile */}
            <button 
              onClick={() => setError('')} 
              className="mt-4 w-full sm:hidden bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm dark:shadow-lg border border-gray-200 dark:border-gray-700">
          {videoId && (
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 text-teal-600 dark:text-teal-400">Video Preview</h2>
              <div className="relative pt-[56.25%] w-full">
                {platform === 'youtube' ? (
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : platform === 'bilibili' && (
                  <>
                    {console.log('Rendering Bilibili iframe with videoId:', videoId)}
                    <iframe
                      ref={iframeRef}
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src={`https://player.bilibili.com/player.html?bvid=${videoId}&high_quality=1&danmaku=0&autoplay=0`}
                      title="Bilibili video player"
                      frameBorder="0"
                      allowFullScreen
                      scrolling="no"
                      onLoad={handleIframeLoad}
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    ></iframe>
                  </>
                )}
              </div>
            </div>
          )}
          {transcript && (
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 text-teal-600 dark:text-teal-400">Transcript</h2>
              <div className="bg-white dark:bg-gray-700 rounded p-3 sm:p-4 max-h-40 sm:max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{transcript}</pre>
              </div>
            </div>
          )}
        </div>
        
        {/* Add CSS for mobile optimization */}
        <style jsx>{`
          @media (max-width: 640px) {
            input, select, button {
              font-size: 16px !important; /* Prevent iOS zoom on focus */
            }
            
            /* Improve touch targets */
            input[type="checkbox"] {
              min-width: 20px;
              min-height: 20px;
            }
            
            /* Add overscroll behavior for smoother mobile scrolling */
            .overflow-y-auto {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

// Helper function to determine step progress
function getStepProgress(step) {
  switch (step) {
    case 'extract': return 0;
    case 'transcript': return 25;
    case 'tutorial': return 50;
    case 'html': return 75;
    default: return 0;
  }
}

export default Content2Learn;